import React, { FC } from "react";

import { Empty } from "./ReportUi";

/** Charts of the reports added 2026-11-05 (no chart library, like ReportChart). */

type Series = { key: string; label: string; color: string };

/**
 * Columns per bucket. `stacked` = series on top of each other (forecast), else side by
 * side (billed vs received, started vs finished). `top` = text above each column.
 */
export const BarColumns: FC<{
  buckets: {
    key: string;
    label: string;
    values: number[];
    top?: string;
    topTone?: "red";
  }[];
  series: Series[];
  stacked?: boolean;
  height?: number;
  format?: (n: number) => string;
}> = ({
  buckets,
  series,
  stacked,
  height = 200,
  format = (n) => String(n),
}) => {
  if (!buckets.length)
    return <Empty text={"Nothing to show for these dates."} />;
  const max = Math.max(
    1,
    ...buckets.map((b) =>
      stacked ? b.values.reduce((s, v) => s + v, 0) : Math.max(0, ...b.values),
    ),
  );
  const bar = (value: number) => Math.round((value / max) * (height - 30));
  return (
    <div className={"overflow-x-auto"}>
      <div style={{ minWidth: buckets.length * 34 }}>
        <div
          className={
            "flex items-end gap-1.5 border-b border-[#DCE2EA] sm:gap-2.5"
          }
          style={{ height }}>
          {buckets.map((b) => (
            <div
              key={b.key}
              role={"img"}
              aria-label={`${b.label}: ${series
                .map((s, i) => `${s.label} ${format(b.values[i] || 0)}`)
                .join(", ")}`}
              className={
                "flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1"
              }>
              <span
                className={`text-[11px] font-bold ${
                  b.topTone === "red" ? "text-[#B42318]" : "text-[#3F4757]"
                }`}>
                {b.top ?? ""}
              </span>
              {stacked ? (
                <div className={"flex w-full max-w-[40px] flex-col-reverse"}>
                  {series.map((s, i) => (
                    <div
                      key={s.key}
                      style={{
                        height: bar(b.values[i] || 0),
                        backgroundColor: s.color,
                      }}
                      className={
                        i === series.length - 1 ? "rounded-t-[4px]" : undefined
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className={"flex items-end gap-[3px]"}>
                  {series.map((s, i) => (
                    <div
                      key={s.key}
                      className={"w-[12px] rounded-t-[4px] sm:w-[14px]"}
                      style={{
                        height: bar(b.values[i] || 0),
                        backgroundColor: s.color,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
        <div className={"mt-1.5 flex gap-1.5 sm:gap-2.5"}>
          {buckets.map((b) => (
            <span
              key={b.key}
              className={
                "min-w-0 flex-1 truncate text-center text-[11.5px] font-semibold text-[#5B6472]"
              }>
              {b.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/** One person's billable / non-billable split with the target marked (Team utilization). */
export const SplitBar: FC<{ pct: number; target: number; label: string }> = ({
  pct,
  target,
  label,
}) => (
  <div
    role={"img"}
    aria-label={label}
    className={
      "relative h-[22px] w-full overflow-hidden rounded-[6px] bg-[#C9BEFF]"
    }>
    <div
      className={"h-full bg-[#6B4EFF]"}
      style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
    />
    <div
      aria-hidden={"true"}
      className={"absolute bottom-[-2px] top-[-2px] w-[2px] bg-[#1F2433]"}
      style={{ left: `calc(${Math.min(100, Math.max(0, target))}% - 1px)` }}
    />
  </div>
);
